/**
 * Pure visual sub-component for a single notification card.
 *
 * Extracted from NotificationPanel so SwipeableNotificationCard can wrap it
 * with a gesture layer without forking the visual.
 *
 * Stays a real <button> so screen readers and keyboard users can activate
 * the notification with Enter / Space exactly as before.
 */

import { Badge } from '@bbpos/ui';
import type { Notification } from '@/services/notification.service';
import {
  NOTIFICATION_TYPE_META,
  toneIconClass,
  relativeTime,
} from '@/lib/notification-constants';
import { GLASS_INTERACTIVE, cn } from '@/constants/design-tokens';

export interface NotificationCardBodyProps {
  notification: Notification;
  onClick: () => void;
  /** Optional content slotted into the right indicator stack (e.g. mini "mark read" button on lg+). */
  trailingAccessory?: React.ReactNode;
  /** The whole message instead of two lines — the full notifications page (T24). */
  fullMessage?: boolean;
}

export function NotificationCardBody({
  notification,
  onClick,
  trailingAccessory,
  fullMessage = false,
}: NotificationCardBodyProps) {
  const meta = NOTIFICATION_TYPE_META[notification.type] ?? NOTIFICATION_TYPE_META.SYSTEM_ANNOUNCEMENT;
  const iconClass = toneIconClass(meta.tone);
  const isUnread = !notification.is_read;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        GLASS_INTERACTIVE,
        'w-full text-left p-3.5 flex items-start gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        isUnread && 'bg-brand-50/30 dark:bg-brand-900/10',
      )}
    >
      {/* Icon */}
      <div className={cn('w-9 h-9 rounded-xl flex items-center justify-center shrink-0', iconClass)}>
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d={meta.iconPath} />
        </svg>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={cn(
          'text-sm',
          isUnread ? 'font-bold text-neutral-900 dark:text-neutral-100' : 'font-medium text-neutral-700 dark:text-neutral-300',
        )}>
          {notification.title}
        </p>
        <p
          className={cn(
            'text-xs text-neutral-500 dark:text-neutral-400 mt-0.5',
            fullMessage ? 'whitespace-pre-line break-words' : 'line-clamp-2',
          )}
        >
          {notification.message}
        </p>
        <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-1">
          {relativeTime(notification.created_at)}
        </p>
      </div>

      {/* Priority / unread indicators */}
      <div className="flex items-center gap-1.5 shrink-0">
        {notification.priority === 'URGENT' && (
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-rose-500" />
          </span>
        )}
        {notification.priority === 'HIGH' && (
          <Badge variant="warning" size="sm">High</Badge>
        )}
        {isUnread && notification.priority !== 'URGENT' && (
          <span className="w-2 h-2 rounded-full bg-brand-500 shrink-0" />
        )}
        {trailingAccessory}
      </div>
    </button>
  );
}
